import { memo, useMemo } from 'react'
import { Button } from '@panwds/react-ui'
import { Link } from 'react-router-dom'
import { _T } from '../../i18n'
import FullscreenError, { NoAccessFullscreenError } from './FullscreenError'
import { openCommonModule, openTenantSwitcher } from '../../framework/actions'
import { hasMicroApp } from '../../framework/microApps'
import { usePageTitle } from '../../framework/title'
import { COMMON_SERVICES_MICRO_APP_NAME, PA_DEFAULT_ROUTE, HUB_URL_KEY } from '../../framework/constants'
import { getFrameworkServiceUrl } from '../../framework/states'
import type { VFC } from 'react'
import type { AuthState } from '../../framework/stateTypes'


const VIEW_MANAGE_TENANT = '/tenant_management'

export const PathNotAvailable: VFC<{ path?: string, location?: { pathname: string } }> = ({
  location,
  path = location?.pathname || window.location.pathname || '/',
}) => {
  const inContent = Boolean(location)
  const button = useMemo(() => {
    if (!inContent || !hasMicroApp(COMMON_SERVICES_MICRO_APP_NAME)) {
      return undefined
    }
    return <Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={() => openCommonModule(VIEW_MANAGE_TENANT)}
    >{_T('Manage Tenant')}</Button>
  }, [inContent])
  return <FullscreenError
    className={inContent ? 'sparky-full-content-error' : undefined}
    message={path === '/' ? _T('Default path is not available') : path}
    button={button}
  >{_T('Path not available')}</FullscreenError>
}

type NoTenantAccessType = {
  auth: Pick<AuthState, 'tsg_name' | 'tsg_id'> | undefined
  isIPRestricted?: boolean
}
export const NoTenantAccess: VFC<NoTenantAccessType> = ({ auth, isIPRestricted = false }) => {
  const button = useMemo(() => {
    if (!hasMicroApp(COMMON_SERVICES_MICRO_APP_NAME)) {
      return undefined
    }
    return <Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={() => openTenantSwitcher(true, auth?.tsg_id)}
    >{_T('Switch Tenant')}</Button>
  }, [auth?.tsg_id])
  const hubUrl = getFrameworkServiceUrl(HUB_URL_KEY)
  const ipRestrictedMessage = <div>
    <div>
      The content you are trying to access is limited to specific IP addresses for this tenant. Seems like your IP address is not on the list.
    </div>
    <div>
      Please reach out to your system admin for support or alternatively Go to <a href={hubUrl}>Hub </a>
      &#x2192; Common Services &#x2192; Trusted IP List to resolve the issue.
    </div>
  </div>
  const message = isIPRestricted ? '' : _T('Please switch to a different tenant or ask the administrator to grant you access.')
  const content = isIPRestricted ? ipRestrictedMessage : _T(`You do not have access to this tenant: ${auth?.tsg_name || auth?.tsg_id || ''}`)
  return <FullscreenError
    className='sparky-full-content-error'
    message={message}
    button={button}
    isIPRestricted={isIPRestricted}
  >{content}</FullscreenError>

}

export const NoPathAvailable: VFC<{}> = () => {
  return <NoAccessFullscreenError className={'sparky-full-content-error'}/>
}

const FawkesNotAvailableComp: VFC<{ from?: string, message?: string, msp?: string }> = ({ message, from, msp }) => {
  usePageTitle('Prisma Access')
  return <FullscreenError
    className={'sparky-full-content-error'}
    message={message || ''}
    button={
      <div className='sparky-fullscreen-error-buttons'>
        <a href={from || PA_DEFAULT_ROUTE} className='sparky-fullscreen-error-button'><Button
          appearance='clear'
        >{_T('Retry')}</Button></a>
        {msp && <Link to={msp} className='sparky-fullscreen-error-button'>
          <Button
            appearance='clear'
          >{_T('Visit Multitenant View')}</Button>
        </Link>}
      </div>
    }
  >
    {_T('Prisma Access application failed to launch, please try switch to a different tenant or launch from hub.')}
    <br />
    {_T('If issue persists please contact Palo Alto Networks support.')}
  </FullscreenError>
}
export const FawkesNotAvailable = memo(FawkesNotAvailableComp)

export default PathNotAvailable
